import { Link, createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { StalledAlertsSection } from './-components/StalledAlertsSection'
import { RothRadarAlertsSection } from './-components/RothRadarAlertsSection'
import { PageTitle } from '@/components/PageTitle'
import { BrandLogo } from '@/components/shared/BrandLogo'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { Fade } from '@/components/animate-ui/primitives/effects/fade'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

const searchSchema = z.object({
  // .catch keeps old ?tab=activity bookmarks working
  tab: z.enum(['alerts', 'roth-radar']).catch('alerts'),
})

export const Route = createFileRoute('/_app/backoffice/integrations/slack/')({
  validateSearch: searchSchema,
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.slack),
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { tab } = Route.useSearch()

  return (
    <div className="py-4">
      <PageTitle title="Slack" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice/integrations">Integrations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Slack</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-4 flex items-center gap-3">
        <BrandLogo domain="slack.com" name="Slack" className="size-8" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Slack
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Automated alerts posted to Slack channels.
          </p>
        </div>
      </div>

      <Fade transition={{ duration: 0.2 }}>
        <Tabs
          value={tab}
          onValueChange={(value) =>
            void navigate({
              search: { tab: value as 'alerts' | 'roth-radar' },
              replace: true,
            })
          }
          className="w-full"
        >
          <TabsList className="h-11 sm:h-9">
            <TabsTrigger value="alerts" className="sm:min-w-28">
              Stalled alerts
            </TabsTrigger>
            <TabsTrigger value="roth-radar" className="sm:min-w-28">
              RothRadar™
            </TabsTrigger>
          </TabsList>
          <TabsContent value="alerts" className="mt-4 w-full">
            <StalledAlertsSection />
          </TabsContent>
          <TabsContent value="roth-radar" className="mt-4 w-full">
            <RothRadarAlertsSection />
          </TabsContent>
        </Tabs>
      </Fade>
    </div>
  )
}
